import React from 'react';
import { Drawer, FloatButton } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import ResizablePanel from './ResizablePanel';

/**
 * Vertical chart / bottom-tabs split.
 * - Desktop: a draggable vertical ResizablePanel (chart on top, tabs below).
 * - Mobile (`stacked`): chart pinned to a fixed height, tabs fill the rest.
 *
 * Expects exactly two children: [chart, bottomTabs].
 */
export const ChartTabsSplit: React.FC<{
  stacked: boolean;
  initialSizes?: number[];
  minSizes?: number[];
  gap?: number;
  /** Mobile chart height as a viewport-height fraction. */
  chartHeightVh?: number;
  chartMinHeight?: number;
  /** Persist the dragged split to localStorage under this key. */
  storageKey?: string;
  children: React.ReactNode;
}> = ({
  stacked,
  initialSizes = [50, 50],
  minSizes = [200, 350],
  gap = 8,
  chartHeightVh = 42,
  chartMinHeight = 240,
  storageKey,
  children,
}) => {
  if (stacked) {
    const [chart, tabs] = React.Children.toArray(children);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap }}>
        <div style={{ flexShrink: 0, height: `${chartHeightVh}vh`, minHeight: chartMinHeight, position: 'relative', overflow: 'hidden' }}>
          {chart}
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
          {tabs}
        </div>
      </div>
    );
  }
  return (
    <ResizablePanel direction="vertical" initialSizes={initialSizes} minSizes={minSizes} gap={gap} storageKey={storageKey}>
      {children as React.ReactNode[]}
    </ResizablePanel>
  );
};

/**
 * Horizontal chart-column / config-column split.
 * - Desktop: a horizontal ResizablePanel (chart left, config right).
 * - Mobile (`stacked`): chart column fills the screen; the config column moves into
 *   a bottom Drawer toggled by a floating button.
 *
 * Expects exactly two children: [chartColumn, configColumn].
 */
export const ChartConfigSplit: React.FC<{
  stacked: boolean;
  drawerOpen: boolean;
  onOpenDrawer: () => void;
  onCloseDrawer: () => void;
  configTitle?: string;
  initialSizes?: number[];
  minSizes?: number[];
  gap?: number;
  /** Persist the dragged split to localStorage under this key. */
  storageKey?: string;
  /** Total height of the chart column on mobile — taller than the viewport so the
   *  page scrolls (chart at top, tabs below). Lets height:100% children resolve. */
  mobileContentHeight?: string;
  children: React.ReactNode;
}> = ({
  stacked,
  drawerOpen,
  onOpenDrawer,
  onCloseDrawer,
  configTitle = 'Trading Parameters',
  initialSizes = [67, 33],
  minSizes = [300, 300],
  gap = 8,
  storageKey,
  mobileContentHeight = '130vh',
  children,
}) => {
  if (stacked) {
    const [chartColumn, configColumn] = React.Children.toArray(children);
    return (
      <>
        {/* Scroll container: fills the viewport and scrolls the taller content below. */}
        <div style={{ flex: 1, minHeight: 0, height: '100%', width: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ display: 'flex', flexDirection: 'column', height: mobileContentHeight, flexShrink: 0 }}>
            {chartColumn}
          </div>
        </div>
        <FloatButton
          icon={<SettingOutlined />}
          type="primary"
          tooltip={configTitle}
          onClick={onOpenDrawer}
          style={{ insetInlineEnd: 16, insetBlockEnd: 16 }}
        />
        <Drawer
          placement="bottom"
          height="88%"
          open={drawerOpen}
          onClose={onCloseDrawer}
          title={configTitle}
          styles={{ body: { padding: 12, display: 'flex', flexDirection: 'column', minHeight: 0 } }}
        >
          {configColumn}
        </Drawer>
      </>
    );
  }
  return (
    <ResizablePanel direction="horizontal" initialSizes={initialSizes} minSizes={minSizes} gap={gap} storageKey={storageKey}>
      {children as React.ReactNode[]}
    </ResizablePanel>
  );
};

/**
 * Same chart-column / config-column behaviour as ChartConfigSplit, but the desktop
 * layout is a plain flexbox row (used by pages that don't want a drag handle, e.g.
 * GridTrading). On mobile the config column moves into a bottom Drawer.
 *
 * Expects exactly two children: [chartColumn, configColumn].
 */
export const FlexConfigSplit: React.FC<{
  stacked: boolean;
  drawerOpen: boolean;
  onOpenDrawer: () => void;
  onCloseDrawer: () => void;
  configTitle?: string;
  /** Style for the desktop flex row container (preserves the page's original layout). */
  containerStyle?: React.CSSProperties;
  /** Total height of the chart column on mobile — taller than the viewport so the page scrolls. */
  mobileContentHeight?: string;
  children: React.ReactNode;
}> = ({
  stacked,
  drawerOpen,
  onOpenDrawer,
  onCloseDrawer,
  configTitle = 'Configuration',
  containerStyle,
  mobileContentHeight = '130vh',
  children,
}) => {
  if (stacked) {
    const [chartColumn, configColumn] = React.Children.toArray(children);
    return (
      <>
        {/* Scroll container: fills the viewport and scrolls the taller content below. */}
        <div style={{ flex: 1, minHeight: 0, height: '100%', width: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ display: 'flex', flexDirection: 'column', height: mobileContentHeight, flexShrink: 0 }}>
            {chartColumn}
          </div>
        </div>
        <FloatButton
          icon={<SettingOutlined />}
          type="primary"
          tooltip={configTitle}
          onClick={onOpenDrawer}
          style={{ insetInlineEnd: 16, insetBlockEnd: 16 }}
        />
        <Drawer
          placement="bottom"
          height="88%"
          open={drawerOpen}
          onClose={onCloseDrawer}
          title={configTitle}
          styles={{ body: { padding: 12, display: 'flex', flexDirection: 'column', minHeight: 0 } }}
        >
          {configColumn}
        </Drawer>
      </>
    );
  }
  return <div style={containerStyle}>{children}</div>;
};
